import { Head, useForm, Link } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { ArrowLeft, Award } from 'lucide-react'
import { FormEvent } from 'react'

interface SelectOption {
  id: number
  name: string
}

interface Props extends PageProps {
  agents: SelectOption[]
}

export default function EquityGrant({ agents }: Props) {
  const { data, setData, post, processing, errors } = useForm({
    agent_id: '',
    credit_amount: '',
    credit_type: 'bonus',
    event_name: '',
    event_date: new Date().toISOString().split('T')[0],
    notes: ''
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    post('/equity_credits/create_grant')
  }

  return (
    <AppLayout>
      <Head title="Grant Equity Credits" />

      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Link href="/equity_credits" className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-chestnut-gold/10 rounded-lg">
              <Award className="h-6 w-6 text-chestnut-gold" />
            </div>
            <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">
              Grant Equity Credits
            </h1>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="card space-y-6">
          <div>
            <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Agent *</label>
            <select
              value={data.agent_id}
              onChange={(e) => setData('agent_id', e.target.value)}
              className="input-field w-full"
              required
            >
              <option value="">Select agent...</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>{agent.name}</option>
              ))}
            </select>
            {errors.agent_id && <p className="mt-1 text-sm text-status-error">{errors.agent_id}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Credit Amount *</label>
              <input
                type="number"
                value={data.credit_amount}
                onChange={(e) => setData('credit_amount', e.target.value)}
                className="input-field w-full"
                step="0.01"
                required
              />
              {errors.credit_amount && <p className="mt-1 text-sm text-status-error">{errors.credit_amount}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Credit Type *</label>
              <select
                value={data.credit_type}
                onChange={(e) => setData('credit_type', e.target.value)}
                className="input-field w-full"
              >
                <option value="production">Production</option>
                <option value="override">Override</option>
                <option value="bonus">Bonus</option>
                <option value="adjustment">Adjustment</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Event Name *</label>
              <input
                type="text"
                value={data.event_name}
                onChange={(e) => setData('event_name', e.target.value)}
                className="input-field w-full"
                placeholder="e.g., Q1 Production Bonus"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Event Date *</label>
              <input
                type="date"
                value={data.event_date}
                onChange={(e) => setData('event_date', e.target.value)}
                className="input-field w-full"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Notes</label>
            <textarea
              value={data.notes}
              onChange={(e) => setData('notes', e.target.value)}
              className="input-field w-full"
              rows={3}
              placeholder="Optional notes about this credit grant..."
            />
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-chestnut-gray">
            <Link href="/equity_credits" className="btn-secondary">Cancel</Link>
            <button type="submit" disabled={processing} className="btn-primary">
              {processing ? 'Granting...' : 'Grant Credits'}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
